/**
 * (Shared: the extension's travel table, the website's Travel page and the server's stock pool all use this.)
 *
 * Stock abroad and travel profits. Two halves, both pure:
 *
 * 1. **Trip profits** (moved here from the extension's core/travel-table.ts in v0.121.0): Torn's travel agency (flight
 *    times and tickets, the defaults below as Torn's agency listed them in October 2026), what each country's shops
 *    have (the server's pooled list), what you can carry, and the profit per item, per trip and per minute flying.
 * 2. **Restock model** (website roadmap S10): from a shop item's stock over time (what Omni-Torn users saw, pooled
 *    anonymously by the server — apps/api/src/travel-stock.ts), when it restocks, how fast it sells out, the stock it
 *    likely has when you'd land, when to leave, and a day plan of trips back to back. Every forecast carries a
 *    confidence: few or irregular sightings give "low", many regular ones "high".
 */

type Obj = Record<string, unknown>;
const obj = (x: unknown): Obj | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Obj) : null);
const num = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) ? x : null);
const str = (x: unknown): string | null => (typeof x === 'string' && x.trim() ? x.trim() : null);

// ---------------------------------------------------------------- trip profits

export type FlightType = 'standard' | 'airstrip' | 'private' | 'business';
export const FLIGHT_TYPES: readonly FlightType[] = ['standard', 'airstrip', 'private', 'business'];

export interface Country {
  /** The country's code (mex, cay, can… — the first three letters of Torn's name). */
  code: string;
  name: string;
  /** One way, seconds, per flight type. */
  seconds: Partial<Record<FlightType, number>>;
  /** Standard ticket price. */
  ticket: number | null;
}

export interface Agency {
  countries: Country[];
  /** Flight types you can take (from the agency answer). */
  available: FlightType[];
}

const row = (code: string, name: string, ticket: number, s: [number, number, number, number]): Country => ({ code, name, ticket, seconds: { standard: s[0], airstrip: s[1], private: s[2], business: s[3] } });

export const DEFAULT_AGENCY: Agency = {
  available: ['standard'],
  countries: [
    row('mex', 'Mexico', 6500, [1440, 1020, 720, 420]),
    row('cay', 'Cayman Islands', 10000, [1980, 1380, 1020, 600]),
    row('can', 'Canada', 9000, [2340, 1620, 1140, 720]),
    row('haw', 'Hawaii', 11000, [7620, 5340, 3780, 2280]),
    row('uni', 'United Kingdom', 18000, [9060, 6360, 4500, 2700]),
    row('arg', 'Argentina', 21000, [9480, 6660, 4740, 2820]),
    row('swi', 'Switzerland', 27000, [9960, 6960, 4980, 3000]),
    row('jap', 'Japan', 32000, [12780, 8940, 6420, 3840]),
    row('chi', 'China', 35000, [13740, 9600, 6840, 4140]),
    row('uae', 'UAE', 32000, [15420, 10800, 7680, 4620]),
    row('sou', 'South Africa', 40000, [16920, 11820, 8460, 5100]),
  ],
};

export interface StockItem {
  id: number;
  name: string;
  qty: number;
  cost: number;
}

export interface Stocks {
  /** Per country code: when someone last saw the shop (Unix s), and the items. */
  byCountry: Record<string, { updated: number | null; items: StockItem[] }>;
}

/** The server's /api/travel/stock (YATA's export shape, which it keeps). */
export function readStocks(body: unknown): Stocks {
  const out: Stocks = { byCountry: {} };
  for (const [code, v] of Object.entries(obj(obj(body)?.stocks) ?? {})) {
    const o = obj(v);
    if (!o) continue;
    const items = (Array.isArray(o.stocks) ? o.stocks : [])
      .map((x): StockItem | null => {
        const i = obj(x);
        const id = num(i?.id);
        const cost = num(i?.cost);
        if (!i || !id || cost === null) return null;
        return { id, name: str(i.name) ?? `Item ${id}`, qty: num(i.quantity) ?? 0, cost };
      })
      .filter((i): i is StockItem => !!i);
    out.byCountry[code] = { updated: num(o.update), items };
  }
  return out;
}

/** Items you can bring back: 10, +5 on any flight but standard, plus your travel-capacity perks. */
export function capacity(type: FlightType, perks: number): number {
  return 10 + (type === 'standard' ? 0 : 5) + Math.max(0, perks);
}

/** "+ 5 Travel items (Large Suitcase)", "+ 2 Travel capacity" … → the extra items they add. */
export function capacityPerks(perks: unknown): number {
  const lines: string[] = [];
  const walk = (x: unknown) => (typeof x === 'string' ? lines.push(x) : Array.isArray(x) ? x.forEach(walk) : obj(x) ? Object.values(obj(x)!).forEach(walk) : undefined);
  walk(perks);
  let n = 0;
  for (const l of lines) {
    const m = /\+\s*(\d+)\s*(?:travel items?|items? (?:while|when) travel|travel capacity)/i.exec(l) ?? /increases?\s+(?:your\s+)?travel (?:items?|capacity)\s+by\s+(\d+)\b(?!\s*%)/i.exec(l);
    if (m) n += Number(m[1]);
  }
  return n;
}

export interface TripRow {
  country: Country;
  item: StockItem;
  updated: number | null;
  value: number | null;
  /** How many you'd bring (capacity, or what's in stock). */
  amount: number;
  profitEach: number | null;
  /** For the whole trip, less the ticket on a standard flight and your selling fee. */
  profitTrip: number | null;
  /** Per minute in the air (there and back). */
  perMinute: number | null;
  /** One way. */
  seconds: number | null;
}

export function tripRows(agency: Agency, stocks: Stocks, valueOf: (id: number) => number | null, type: FlightType, cap: number, feePct: number): TripRow[] {
  const rows: TripRow[] = [];
  for (const country of agency.countries) {
    const s = stocks.byCountry[country.code];
    if (!s) continue;
    const seconds = country.seconds[type] ?? null;
    for (const item of s.items) {
      const value = valueOf(item.id);
      const amount = Math.max(0, Math.min(cap, item.qty));
      const each = value === null ? null : value * (1 - feePct / 100) - item.cost;
      const trip = each === null ? null : each * amount - (type === 'standard' ? (country.ticket ?? 0) : 0);
      rows.push({ country, item, updated: s.updated, value, amount, profitEach: each === null ? null : Math.round(each), profitTrip: trip === null ? null : Math.round(trip), perMinute: trip !== null && seconds ? Math.round(trip / ((seconds * 2) / 60)) : null, seconds });
    }
  }
  return rows;
}

export interface TripEstimate {
  /** Items you'd bring (capacity, or the stock if less). */
  amount: number;
  /** Profit on each item, after your selling fee. */
  each: number;
  /** The whole trip, less the ticket on a standard flight. */
  trip: number;
  /** There and back, seconds. */
  roundTrip: number;
  perHour: number;
  /** Trips back to back in 24 hours (with `shopSeconds` in the shop each time), and what they make. */
  perDay: { trips: number; profit: number };
}

/**
 * One trip, typed in (the website's travel profit estimator, after weav3r's): what you buy abroad and sell back home, the
 * way `tripRows` reckons it — profit per item after the selling fee, × what you carry, less the ticket on a standard
 * flight — per hour flying and per day of trips back to back.
 */
export function tripEstimate(country: Country, type: FlightType, cap: number, buy: number, sell: number, feePct: number, stock: number | null = null, shopSeconds = 120): TripEstimate | null {
  const oneWay = country.seconds[type];
  if (!oneWay) return null;
  const amount = Math.max(0, Math.min(Math.floor(cap), stock === null ? Infinity : Math.floor(stock)));
  const each = Math.max(0, sell) * (1 - Math.max(0, Math.min(100, feePct)) / 100) - Math.max(0, buy);
  const trip = each * amount - (type === 'standard' ? (country.ticket ?? 0) : 0);
  const roundTrip = oneWay * 2;
  const trips = Math.floor((24 * 3600) / (roundTrip + Math.max(0, shopSeconds)));
  return { amount, each: Math.round(each), trip: Math.round(trip), roundTrip, perHour: Math.round(trip / (roundTrip / 3600)), perDay: { trips, profit: Math.round(trip * trips) } };
}

/** "1h 34m" / "24m". */
export function flightText(seconds: number): string {
  const m = Math.round(seconds / 60);
  return m >= 60 ? `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m` : `${m}m`;
}

// ---------------------------------------------------------------- restock model

/** One sighting of a shop item's stock: when (ms) and how many. */
export interface StockPoint {
  at: number;
  qty: number;
}

/** A restock seen between two sightings: somewhere in [from, to], `at` the middle. */
export interface Restock {
  at: number;
  from: number;
  to: number;
  /** Stock right after (the later sighting). */
  qty: number;
}

export type Confidence = 'none' | 'low' | 'medium' | 'high';

export interface RestockModel {
  /** Restocks seen in the history. */
  restocks: number;
  /** Time between restocks (ms), or null with fewer than two seen. */
  interval: number | null;
  /** How irregular the gaps are: their average distance from the interval (0 = like clockwork; 0.5 = ±50%). */
  spread: number | null;
  /** The last restock seen (ms). */
  lastRestock: number | null;
  /** Usual stock right after a restock. */
  peak: number | null;
  /** Items bought per hour while it's in stock (null: never seen selling). */
  drainPerHour: number | null;
  /** The next restock expected: middle, earliest and latest (ms); null without an interval. */
  next: { at: number; early: number; late: number } | null;
  /** 0–1, and in words. */
  score: number;
  confidence: Confidence;
}

const MIN = 60_000;
const HOUR = 60 * MIN;

const median = (xs: readonly number[]): number | null => {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
};

/** Sightings in time order, one per moment (a later one at the same ms wins), sane numbers only. */
function clean(points: readonly StockPoint[]): StockPoint[] {
  const s = points.filter((p) => Number.isFinite(p.at) && Number.isFinite(p.qty) && p.qty >= 0).sort((a, b) => a.at - b.at);
  const out: StockPoint[] = [];
  for (const p of s) {
    if (out.length && out[out.length - 1]!.at === p.at) out[out.length - 1] = p;
    else out.push(p);
  }
  return out;
}

/**
 * The restocks in a history: wherever the stock went up between two sightings (players can't sell to a shop abroad,
 * so a rise is always a restock). Rises within 5 minutes of the last one count as the same restock.
 */
export function findRestocks(points: readonly StockPoint[]): Restock[] {
  const p = clean(points);
  const out: Restock[] = [];
  for (let i = 1; i < p.length; i++) {
    const a = p[i - 1]!;
    const b = p[i]!;
    if (b.qty <= a.qty) continue;
    const last = out[out.length - 1];
    if (last && a.at - last.to < 5 * MIN) {
      last.to = b.at;
      last.at = (last.from + last.to) / 2;
      last.qty = b.qty;
      continue;
    }
    out.push({ at: (a.at + b.at) / 2, from: a.at, to: b.at, qty: b.qty });
  }
  return out;
}

/**
 * Items bought per hour while in stock: the drops between sightings in the last `windowMs` (12 h), over the time they
 * took. A drop that ends at 0 is left out (it sold out somewhere in between, so its pace is unknown).
 */
export function drainRate(points: readonly StockPoint[], now: number, windowMs = 12 * HOUR): number | null {
  const p = clean(points).filter((x) => x.at >= now - windowMs);
  let sold = 0;
  let time = 0;
  for (let i = 1; i < p.length; i++) {
    const a = p[i - 1]!;
    const b = p[i]!;
    if (b.qty > a.qty || a.qty === 0 || b.qty === 0) continue;
    sold += a.qty - b.qty;
    time += b.at - a.at;
  }
  if (time < 10 * MIN) return null;
  return (sold / time) * HOUR;
}

/**
 * The restock model for one shop item from its history. The interval is the usual gap between restocks; a gap about
 * two or three times that (restocks nobody saw) counts as that many gaps. The confidence grows with the restocks seen,
 * how regular they are, and how closely each was seen (a restock known only to within an hour says little).
 */
export function restockModel(points: readonly StockPoint[], now: number): RestockModel {
  const p = clean(points);
  const rs = findRestocks(p);
  const drain = drainRate(p, now);
  const peak = median(rs.map((r) => r.qty));
  const lastRestock = rs.length ? rs[rs.length - 1]!.at : null;
  const none: RestockModel = { restocks: rs.length, interval: null, spread: null, lastRestock, peak, drainPerHour: drain, next: null, score: 0, confidence: rs.length ? 'low' : 'none' };
  if (rs.length < 2) return none;
  const raw = rs.slice(1).map((r, i) => r.at - rs[i]!.at);
  const base = Math.min(...raw.filter((g) => g >= Math.max(5 * MIN, 0.4 * median(raw)!)));
  if (!Number.isFinite(base) || base <= 0) return none;
  // A gap about 2 or 3 times the shortest (within 25%) is that many intervals (restocks nobody saw); others stay as
  // they are, and count against the regularity.
  const per: number[] = [];
  for (const g of raw) {
    const k = Math.round(g / base);
    per.push(k >= 2 && k <= 3 && Math.abs(g - k * base) <= 0.25 * k * base ? g / k : g);
  }
  const interval = median(per)!;
  // How far the gaps stray from it, on average (each counted at most as 100%).
  const spread = per.reduce((a, g) => a + Math.min(1, Math.abs(g - interval) / interval), 0) / per.length;
  const blur = median(rs.map((r) => (r.to - r.from) / 2))!;
  const evidence = Math.min(1, per.length / 6);
  const regular = Math.max(0, 1 - spread * 2.5);
  const sharp = Math.max(0, 1 - blur / interval);
  // Long since anyone saw a restock: the guess drifts.
  const steps = Math.max(1, Math.ceil((now - lastRestock!) / interval));
  const fresh = 1 / Math.sqrt(Math.max(1, steps - 1));
  const score = Math.round(evidence * regular * sharp * fresh * 100) / 100;
  const confidence: Confidence = score >= 0.6 ? 'high' : score >= 0.3 ? 'medium' : 'low';
  const at = lastRestock! + steps * interval;
  const half = Math.min(interval / 2, Math.max(blur, spread * interval * Math.sqrt(steps), MIN));
  return { restocks: rs.length, interval, spread, lastRestock, peak, drainPerHour: drain, next: { at, early: at - half, late: at + half }, score, confidence };
}

/**
 * The stock likely there at time `t`, from the last sighting `last` and the model: the restocks expected since then
 * fill it to the usual peak, and buyers take it down at the usual pace. Null when nothing is known.
 */
export function stockAt(model: RestockModel, last: StockPoint | null, t: number): number | null {
  if (!last) return null;
  let base = last.qty;
  let from = last.at;
  if (model.interval && model.lastRestock !== null && model.peak !== null && t > last.at) {
    // The latest expected restock after the sighting and no later than t.
    const k = Math.floor((t - model.lastRestock) / model.interval);
    const r = model.lastRestock + k * model.interval;
    if (k >= 1 && r > last.at) {
      base = model.peak;
      from = r;
    }
  }
  if (t <= from) return base;
  const drain = model.drainPerHour ?? 0;
  return Math.max(0, Math.round(base - (drain * (t - from)) / HOUR));
}

/** When the stock (at its pace) runs out after `from`, or null if it isn't selling or is already out. */
export function emptyAt(model: RestockModel, last: StockPoint | null, now: number): number | null {
  const q = stockAt(model, last, now);
  if (!q || !model.drainPerHour) return null;
  return now + (q / model.drainPerHour) * HOUR;
}

export interface Departure {
  /** Leave Torn at (ms): `now` means "leave now". */
  leaveAt: number;
  landAt: number;
  /** The stock expected when you land. */
  stock: number;
  /** Minutes until you should leave (0 = now). */
  waitMin: number;
}

/**
 * The departure planner: the earliest time to leave (from `now`, minute by minute, up to `horizonMs` ahead) so that at
 * least `want` items are expected in stock when you land after `oneWayMs`. Null when no time in the horizon works.
 */
export function departure(model: RestockModel, last: StockPoint | null, now: number, oneWayMs: number, want: number, horizonMs = 24 * HOUR): Departure | null {
  if (!last || oneWayMs <= 0) return null;
  const need = Math.max(1, want);
  for (let leave = now; leave <= now + horizonMs; leave += MIN) {
    const stock = stockAt(model, last, leave + oneWayMs);
    if (stock !== null && stock >= need) return { leaveAt: leave, landAt: leave + oneWayMs, stock, waitMin: Math.round((leave - now) / MIN) };
    // Nothing will come back without a restock model: no use waiting.
    if (!model.interval && leave > now) break;
  }
  return null;
}

export interface PlanOption {
  /** "<country>:<item id>". */
  key: string;
  country: string;
  item: string;
  /** Profit per item once sold back home (after your selling fee). */
  each: number;
  /** The ticket, if you pay one. */
  ticket: number;
  oneWayMs: number;
  model: RestockModel;
  last: StockPoint | null;
}

export interface PlanTrip {
  key: string;
  country: string;
  item: string;
  leaveAt: number;
  landAt: number;
  backAt: number;
  amount: number;
  profit: number;
  confidence: Confidence;
}

/**
 * A day plan: trips back to back from `from` until `until` (each must be home by then), picking each time the trip
 * that makes the most per hour counted from now — waiting for a restock counts as time spent. One item per trip (the
 * best one); `shopMs` is the time abroad buying (2 minutes). At most 40 trips.
 */
export function dayPlan(options: readonly PlanOption[], cap: number, from: number, until: number, shopMs = 2 * MIN): PlanTrip[] {
  const out: PlanTrip[] = [];
  let t = from;
  while (out.length < 40) {
    let best: { trip: PlanTrip; rate: number } | null = null;
    for (const o of options) {
      if (o.each <= 0) continue;
      const d = departure(o.model, o.last, t, o.oneWayMs, Math.min(cap, o.model.peak ?? cap), Math.max(0, until - t));
      if (!d) continue;
      const backAt = d.landAt + shopMs + o.oneWayMs;
      if (backAt > until) continue;
      const amount = Math.min(cap, d.stock);
      const profit = Math.round(amount * o.each - o.ticket);
      if (profit <= 0) continue;
      const rate = profit / (backAt - t);
      if (!best || rate > best.rate) best = { trip: { key: o.key, country: o.country, item: o.item, leaveAt: d.leaveAt, landAt: d.landAt, backAt, amount, profit, confidence: o.model.confidence }, rate };
    }
    if (!best) break;
    out.push(best.trip);
    t = best.trip.backAt;
  }
  return out;
}
